/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { inject } from "mobx-react";
import { PageByPageView } from "components/document/page/PageByPageView";
import { SinglePageContainer } from "containers/document/SinglePageContainer";
import { getViewPortHeight, getViewPortWidthWithoutPagesViewScrollbar } from "selectors/documentViewSelectors";
import { getCurrentPageIndex } from "selectors/pageNumber";
import { triggerPagesInViewEvent } from "publicApis/Events/events";
import { getDocViewBgColor } from "selectors/documentSelectors";
import { pageContentHandler } from "pageContentHandler";
import { isTouchDevice } from "dc-preview-utils/src/Utils/DeviceUtils";
import { isVeritcallyScrollable, isHorizontallyScrollable, thumbnailsInView } from "selectors/pagedViewSelectors";
import { isFullScreenMode, isFitOneFullPageMode } from "selectors/documentSelectors";
import { getPageHeight } from "selectors/pageSelectors";
import { getCurrentPageZoomLevel } from "selectors/zoom";
import { shouldShowScrubber, showScrubberMidDiv } from "selectors/pagedViewState";
import { getScrollTopAsPerScrubber, getPagePositionFromTop } from "selectors/pageByPageSelectors";
import { overlayEvents } from "overlays/OverlayEvents";
import { exitFullScreenView } from "utils/PageByPageViewUtils";
import { fullScreenExitReason } from "constants/enums";
import { getQuickToolsVisibility } from "selectors/documentSelectors";
import { isAccEnabledByDefault } from "selectors/accessibilitySelectors";
import { PAGE_BY_PAGE_WINDOW_SIZE } from "constants/constants";

const mapStoreToProps = (stores, ownProps) => {
  const pageIndex = getCurrentPageIndex(stores);
  return {
    PageContainer: SinglePageContainer,
    currPageIndex: pageIndex,
    setFocusMethod: ownProps.setFocusMethod,
    bgColor: getDocViewBgColor(stores),
    onPagesInViewChange: pagesInView => {
      if (isFullScreenMode(stores)) {
        stores.documentStore.loadPagesInView(pagesInView);
      } else {
        stores.documentStore.reloadPages(pagesInView);
      }
      triggerPagesInViewEvent(stores);
      stores.documentStore.setPagesInDOM(pagesInView);
    },
    parentViewHeight: getViewPortHeight(stores),
    setElementHandle: handle => {
      stores.pagedViewState.setPagesViewElementHandle(handle);
    },
    handleWheelEvent: evt => {
      // evt.ctrlKey is true in case of pinching through trackpad,
      // safety check !evt.ctrlKey is added to avoid page switching on pinch
      if (!evt.ctrlKey) {
        if (Math.abs(evt.deltaY) > Math.abs(evt.deltaX) && !isVeritcallyScrollable(stores)) {
          if (evt.deltaY > 0) stores.pagedViewState.goToNextPage();
          else if (evt.deltaY < 0) stores.pagedViewState.goToPrevPage();
        } else if (Math.abs(evt.deltaX) > Math.abs(evt.deltaY) && !isHorizontallyScrollable(stores)) {
          if (evt.deltaX > 0) stores.pagedViewState.goToNextPage();
          else if (evt.deltaX < 0) stores.pagedViewState.goToPrevPage();
        }
      }
    },
    handleSwipe: evt => {
      if (isTouchDevice() && isFullScreenMode(stores)) {
        if (Math.abs(evt.deltaY) > Math.abs(evt.deltaX) && !isVeritcallyScrollable(stores)) {
          if (evt.deltaY < 0) stores.pagedViewState.goToNextPage();
          else if (evt.deltaY > 0) stores.pagedViewState.goToPrevPage();
        } else if (Math.abs(evt.deltaX) > Math.abs(evt.deltaY) && !isHorizontallyScrollable(stores)) {
          if (evt.deltaX < 0) stores.pagedViewState.goToNextPage();
          else if (evt.deltaX > 0) stores.pagedViewState.goToPrevPage();
        }
        evt.preventDefault();
      }
    },
    handleClick: evt => {
      isFullScreenMode(stores) && pageContentHandler.handleClickInFullScreeView(evt, pageIndex, stores);
      overlayEvents.trigger("documentclick", evt);
    },
    crossIconClickHandler: evt => {
      exitFullScreenView(stores, fullScreenExitReason.CROSS_ICON);
      evt.stopPropagation();
    },
    onScroll: evt => {
      const scrollEvent = evt.target ? evt : { target: evt };
      overlayEvents.trigger("pagesViewScrolled", scrollEvent);
    },
    onScrollEnd: evt => {
      overlayEvents.trigger("pagesViewScrollEnd", evt);
      triggerPagesInViewEvent(stores);
    },
    addInListOfVisitedPages: () => stores.pageByPageStore.addInListOfVisitedPages(pageIndex),
    pageHeight: getPageHeight(stores, pageIndex),
    setPagePositionFromTop: value => stores.pageByPageStore.setPagePositionFromTop(value),
    zoomlevel: getCurrentPageZoomLevel(stores),
    isFullScreenMode: isFullScreenMode(stores),
    isFitOneFullPageMode: isFitOneFullPageMode(stores),
    shouldShowScrubber: shouldShowScrubber(stores),
    showScrubberMidDiv: showScrubberMidDiv(stores),
    scrollTopAsPerScrubber: getScrollTopAsPerScrubber(stores),
    pagePositionFromTop: getPagePositionFromTop(stores),
    viewPortWidth: getViewPortWidthWithoutPagesViewScrollbar(stores),
    showQuickTools: getQuickToolsVisibility(stores),
    thumbnailsInView: thumbnailsInView(stores),
    pageByPageWindowSize: isAccEnabledByDefault(stores) ? PAGE_BY_PAGE_WINDOW_SIZE : 0
  };
};

const PageByPageViewContainer = inject(mapStoreToProps)(PageByPageView);

export { PageByPageViewContainer };
